import { useState } from 'react';
import { App, Button, Flex, Input, Space, Typography } from 'antd';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { LinesGrid } from '@/shared/ui/LinesGrid';
import type { LinesGridColumn } from '@/shared/ui/LinesGrid';
import { IntInput } from '@/shared/ui/inputs';
import { ImagePicker } from '@/shared/ui/ImagePicker';
import { photoFailureMessage, useCreateCategoryMutation, useListCategoriesQuery, useUploadPendingImages } from '@/entities/category';
import { translateApiError } from '@/shared/api/errorToMessage';

export interface CategoryCreateWindowProps {
  /** Все строки таблицы уходят в одну и ту же корневую группу — выбора группы построчно нет. */
  parentId: string;
  onClose: () => void;
}

interface Row {
  key: string;
  name: string;
  pcsPerBox: number | null;
  /** Необязательное фото: уходит на сервер после создания категории, когда появится её id. */
  image: File | null;
}

let keySeq = 0;
function emptyRow(): Row {
  keySeq += 1;
  return { key: `cat${keySeq}-${Date.now()}`, name: '', pcsPerBox: null, image: null };
}

/** §2.1: добавление основных категорий одной группы — плавающее окно с табличной формой (тот же
 *  приём, что у `CounterpartyCreateWindow`/документов): несколько строк за один заход, строка
 *  снизу добавляется автоматически по заполнению «Название», пустые/неполные строки не сохраняются. */
export function CategoryCreateWindow({ parentId, onClose }: CategoryCreateWindowProps) {
  const { message } = App.useApp();
  const { data: categories = [] } = useListCategoriesQuery({ includeArchived: true });
  const root = categories.find((c) => c.id === parentId);
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [createCategory, { isLoading }] = useCreateCategoryMutation();
  const uploadPendingImages = useUploadPendingImages();

  function addRow() {
    setRows((prev) => [...prev, emptyRow()]);
  }

  function removeRow(key: string) {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  }

  function updateName(key: string, rowIndex: number, value: string) {
    setRows((prev) => {
      const wasEmpty = !prev[rowIndex]?.name;
      const next = prev.map((r) => (r.key === key ? { ...r, name: value } : r));
      if (rowIndex === prev.length - 1 && wasEmpty && value.trim()) {
        return [...next, emptyRow()];
      }
      return next;
    });
  }

  function updatePcsPerBox(key: string, value: number | null) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, pcsPerBox: value } : r)));
  }

  function updateImage(key: string, image: File | null) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, image } : r)));
  }

  const filled = rows.filter((r) => r.name.trim() && r.pcsPerBox != null);
  // Строка с названием, но без верного N: сохранять остальные и молча пропускать её нельзя —
  // владелец решит, что товар добавлен. N после первого проведённого документа уже не исправить.
  const incomplete = rows.filter((r) => r.name.trim() && r.pcsPerBox == null);

  async function handleSave() {
    if (filled.length === 0 || incomplete.length > 0) return;
    try {
      const created = await Promise.all(
        filled.map((r) => createCategory({ name: r.name.trim(), parentId, pcsPerBox: r.pcsPerBox ?? undefined }).unwrap()),
      );
      // Фото не обязательно и не должно мешать созданию: категории уже есть, неудачи — только предупреждение.
      const pending = filled.flatMap((r, i) => (r.image && created[i] ? [{ id: created[i].id, name: r.name.trim(), file: r.image }] : []));
      const failed = pending.length > 0 ? await uploadPendingImages(pending) : [];
      message.success(filled.length > 1 ? `Добавлено категорий: ${filled.length}` : 'Категория добавлена');
      if (failed.length > 0) message.warning(photoFailureMessage(failed, 'категории'));
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  const columns: LinesGridColumn<Row>[] = [
    {
      key: 'name',
      title: 'Название',
      width: 260,
      render: (row, rowIndex) => (
        <Input
          className="nav-cell"
          data-nav-row={rowIndex}
          data-nav-col={0}
          value={row.name}
          autoFocus={rowIndex === 0}
          onChange={(e) => updateName(row.key, rowIndex, e.target.value)}
        />
      ),
    },
    {
      key: 'pcsPerBox',
      title: 'Штук в коробке (N)',
      width: 140,
      align: 'right',
      render: (row, rowIndex) => (
        <IntInput value={row.pcsPerBox} onChange={(v) => updatePcsPerBox(row.key, v)} navRow={rowIndex} navCol={1} />
      ),
    },
    {
      // Не `nav-cell`: выбор файла не входит в навигацию Enter/стрелками по сетке.
      key: 'photo',
      title: 'Фото',
      width: 80,
      render: (row) => (
        <ImagePicker
          file={row.image}
          size={24}
          title="Фото (необязательно)"
          onChange={(image) => updateImage(row.key, image)}
          onReject={(reason) => void message.warning(reason)}
        />
      ),
    },
  ];

  return (
    <FloatingWindow
      kind="categoryCreate"
      title={`Новые категории: ${root?.name ?? ''}`}
      onClose={onClose}
      footer={
        <Space>
          <Button type="primary" loading={isLoading} disabled={filled.length === 0 || incomplete.length > 0} onClick={() => void handleSave()}>
            Сохранить{filled.length > 1 ? ` (${filled.length})` : ''}
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      }
    >
      <Flex vertical gap={8}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Заполните название и N — строка снизу добавится автоматически. Пустые строки не сохраняются.
        </Typography.Text>
        {incomplete.length > 0 ? (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            Укажите «Штук в коробке» — целое число от 1 — у каждой строки с названием: {incomplete.map((r) => `«${r.name.trim()}»`).join(', ')}
          </Typography.Text>
        ) : null}
        <LinesGrid lines={rows} columns={columns} getRowKey={(r) => r.key} onAddRow={addRow} onRemoveRow={removeRow} />
      </Flex>
    </FloatingWindow>
  );
}
